import {createSelector} from 'reselect'
import isEmpty from 'lodash/isEmpty.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import {isPresent, isNumeric} from '../../../common/utils.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import TextInput from '../../../common/components/TextInput.js'
import TextArea from '../../../common/components/TextArea.js'
import DatePicker from '../../../common/components/DatePicker.js'
import CurrencyInput from '../../../common/components/CurrencyInput.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {refreshOrderList} from '../orderListActions.js'
import {saveOrder, orderSelector} from '../../../data/orders.js'

export const MODAL_FORM = 'ORDER_TEXT_FIELD_MODAL'

export function showOrderCustomerNotesModal(orderNumber) {
  const order = orderSelector(getState(), {orderNumber})

  showOrderTextFieldModal({
    orderNumber,
    value: order.notes_from_customer,
    display: 'Customer Notes',
    apiProp: 'notes_from_customer',
    inputType: 'textarea',
    SubComponent() {
      return (
        <div className="alert alert--standard align-center margin-top-10">
          <p className="fs-n1 lh-md margin-bottom-5">
            <strong>
              Any text entered above will appear on the order’s packing list.
            </strong>
          </p>
          <p className="fs-n1 lh-md margin-bottom-0">
            If you want to communicate internally with other members of your
            team, consider leaving an internal note or a timeline comment
            instead.
          </p>
        </div>
      )
    },
  })
}

export function showOrderInternalNotesModal(orderNumber) {
  const order = orderSelector(getState(), {orderNumber})

  showOrderTextFieldModal({
    orderNumber,
    value: order.internal_notes,
    display: 'Internal Notes',
    apiProp: 'internal_notes',
    inputType: 'textarea',
  })
}

export function showOrderShipByDateModal(orderNumber) {
  const order = orderSelector(getState(), {orderNumber})

  showOrderTextFieldModal({
    orderNumber,
    value: order.ship_by_date,
    display: 'Ship By Date',
    apiProp: 'ship_by_date',
    inputType: 'date',
  })
}

export function showOrderDeliverByDateModal(orderNumber) {
  const order = orderSelector(getState(), {orderNumber})

  showOrderTextFieldModal({
    orderNumber,
    value: order.deliver_by_date,
    display: 'Deliver By Date',
    apiProp: 'deliver_by_date',
    inputType: 'date',
  })
}

export function showOrderTextFieldModal({
  orderNumber,
  value,
  display,
  apiProp,
  isRequired,
  needsOrderListRefresh,
  inputType,
  SubComponent,
}) {
  setForm(MODAL_FORM, {
    orderNumber,
    apiProp,
    value:
      inputType === 'date'
        ? value
          ? new Date(value)
          : null
        : isPresent(value)
          ? value
          : '',
    display,
    isRequired: !!isRequired,
    needsOrderListRefresh: !!needsOrderListRefresh,
    inputType: inputType || 'text',
    isSaving: false,
    serverError: null,
    SubComponent,
  })
}

export function updateModalForm(...args) {
  return updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  return removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  ({value, display, isRequired, inputType}) => {
    const errors = {}

    if (isRequired && !isPresent(value)) {
      errors.value = `${display} is required`
    }

    if (inputType === 'currency' && !isNumeric(value)) {
      errors.value = `${display} must be a number`
    }

    return errors
  },
)

export async function saveOrderTextField() {
  try {
    const {
      orderNumber,
      value,
      display,
      apiProp,
      needsOrderListRefresh,
      inputType,
    } = modalFormSelector(getState())

    const params = {
      [apiProp]:
        inputType === 'date'
          ? value
            ? value.toISOString()
            : null
          : inputType === 'currency'
            ? Number(value)
            : value,
    }

    updateModalForm({isSaving: true, serverError: null})

    await saveOrder(orderNumber, params)

    closeModal()

    showMessageToast(`Saved order ${display.toLowerCase()}`)

    if (needsOrderListRefresh) {
      await refreshOrderList()
    }
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function OrderTextFieldModal({form}) {
  const errors = useSelector(errorsSelector)
  const hasErrors = !isEmpty(errors)

  return (
    <ConfirmModal
      title={`Edit Order ${form.display}`}
      modalSize="sm"
      onConfirm={() => saveOrderTextField()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={hasErrors}
      error={form.serverError}
      preventInnerScroll
    >
      <div className="list__item--form list__item--no-style margin-bottom-20">
        <div className="fs-01">
          <strong>{form.orderNumber}</strong>
        </div>
      </div>
      {form.inputType === 'text' ? (
        <TextInput
          className="margin-bottom-0"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : form.inputType === 'textarea' ? (
        <TextArea
          className="fs-00 lh-md margin-bottom-0"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : form.inputType === 'date' ? (
        <div className="w-50">
          <DatePicker
            id="order_param_modal"
            selected={form.value}
            onChange={(value) =>
              updateModalForm({
                value,
              })
            }
          />
        </div>
      ) : form.inputType === 'currency' ? (
        <CurrencyInput
          className="biggie margin-bottom-0"
          width="sm"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : null}
      {form.SubComponent && <form.SubComponent form={form} />}
    </ConfirmModal>
  )
}

export default onlyIfForm(OrderTextFieldModal, modalFormSelector)
